import { useState } from "react";
import { MenuIcon, ShoppingBagIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import I18NLink from "@/components/i18n-link";
import { LanguageSwitcher } from "@/components/language-switcher";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { getTextColor } from "@/lib/utils";
import { useCartSheet } from "@/hooks/storefront/use-cart-sheet";
import { useStorefrontCart } from "@/hooks/storefront/use-storefront-cart";
import { StorefrontCartSheet } from "./storefront-cart-sheet";

/**
 * The webshop's header: logo, navigation and the cart. The cart lives here
 * rather than in a sidebar, so it is one tap away on every page.
 */
export function StorefrontHeader({
  logoUrl,
  companyName,
}: {
  logoUrl?: string;
  companyName: string;
}) {
  const { t } = useTranslation("translation");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: websiteConfig } = useQuery(websiteConfigQueryOptions());
  const headerColor = websiteConfig?.headerColor ?? "#ffffff";

  return (
    <header
      style={{ backgroundColor: headerColor, color: getTextColor(headerColor) }}
      className="sticky top-0 z-40 border-b border-black/5 px-2 sm:px-4"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-2 lg:h-20 lg:gap-8">
        <button
          type="button"
          onClick={() => setIsMenuOpen(true)}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-black/5 lg:hidden"
          aria-label={t("Storefront.Header.open_menu")}
        >
          <MenuIcon className="size-6" />
        </button>

        <div className="flex min-w-0 flex-1 justify-center lg:flex-none lg:justify-start">
          <I18NLink to="/" className="flex min-h-11 items-center">
            {logoUrl ? (
              <img
                alt={companyName}
                src={logoUrl}
                className="max-h-10 w-auto object-contain lg:max-h-12"
              />
            ) : (
              <span className="truncate text-xl font-bold lg:text-2xl">
                {companyName}
              </span>
            )}
          </I18NLink>
        </div>

        <nav className="hidden flex-1 items-center gap-8 lg:flex">
          <I18NLink to="/" className="text-base font-medium hover:opacity-70">
            {t("Storefront.Header.shop")}
          </I18NLink>
          <I18NLink
            to="/#contact-info"
            className="text-base font-medium hover:opacity-70"
          >
            {t("contact")}
          </I18NLink>
        </nav>

        <div className="flex shrink-0 items-center gap-1 lg:gap-4">
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>
          <StorefrontCartButton />
        </div>
      </div>

      <Drawer
        direction="left"
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
      >
        <DrawerContent className="data-[vaul-drawer-direction=left]:w-[85vw] data-[vaul-drawer-direction=left]:sm:max-w-sm">
          <DrawerHeader className="flex flex-row items-center justify-between border-b">
            <DrawerTitle>{companyName}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {t("Storefront.Header.menu_description")}
            </DrawerDescription>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
              aria-label={t("Storefront.Header.close_menu")}
            >
              <XIcon className="size-5" />
            </DrawerClose>
          </DrawerHeader>
          <nav className="flex flex-col p-2">
            <I18NLink
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className="flex min-h-12 items-center rounded-lg px-3 text-lg font-medium hover:bg-gray-100"
            >
              {t("Storefront.Header.shop")}
            </I18NLink>
            <I18NLink
              to="/#contact-info"
              onClick={() => setIsMenuOpen(false)}
              className="flex min-h-12 items-center rounded-lg px-3 text-lg font-medium hover:bg-gray-100"
            >
              {t("contact")}
            </I18NLink>
          </nav>
          <div className="mt-auto border-t p-4">
            <LanguageSwitcher />
          </div>
        </DrawerContent>
      </Drawer>

      <StorefrontCartSheet />
    </header>
  );
}

function StorefrontCartButton() {
  const { t } = useTranslation("translation");
  const setCartOpen = useCartSheet((state) => state.setOpen);
  const { itemCount } = useStorefrontCart();

  return (
    <button
      type="button"
      onClick={() => setCartOpen(true)}
      className="relative flex size-11 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-black/5"
      aria-label={t("Storefront.Cart.open", { count: itemCount })}
    >
      <ShoppingBagIcon className="size-6" />
      {itemCount > 0 && (
        <span className="absolute right-0.5 top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-black px-1 text-xs font-bold text-white ring-2 ring-white">
          {itemCount > 99 ? "99+" : itemCount}
        </span>
      )}
    </button>
  );
}
